import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './flexboxgrid.css';

/**
 * @typedef {Object} ContainerProps
 * @property {React.ReactNode} children
 * @property {boolean} [fullWidth]
 * @property {string} [className]
 */

/**
 * @type {React.ForwardRefExoticComponent<ContainerProps & React.RefAttributes<HTMLDivElement>>}
 */
const Container = forwardRef(function GridContainer(
  /** @type {ContainerProps} */
  { children, fullWidth, className },
  /** @type {React.ForwardedRef<HTMLDivElement>} */
  ref,
) {
  return (
    <div
      ref={ref}
      className={`container-fluid ${
        fullWidth ? ' full-width' : ' container'
      } ${className}`}
    >
      {children}
    </div>
  );
});

Container.propTypes = {
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

Container.defaultProps = {
  fullWidth: false,
  className: '',
};

/**
 * @typedef {Object} RowProps
 * @property {React.ReactNode} children
 * @property {boolean} [start]
 * @property {boolean} [center]
 * @property {boolean} [end]
 * @property {boolean} [top]
 * @property {boolean} [middle]
 * @property {boolean} [bottom]
 * @property {boolean} [mobileStart]
 * @property {boolean} [mobileCenter]
 * @property {boolean} [mobileEnd]
 * @property {boolean} [mobileTop]
 * @property {boolean} [mobileMiddle]
 * @property {boolean} [mobileBottom]
 * @property {boolean} [tabletStart]
 * @property {boolean} [tabletCenter]
 * @property {boolean} [tabletEnd]
 * @property {boolean} [tabletTop]
 * @property {boolean} [tabletMiddle]
 * @property {boolean} [tabletBottom]
 * @property {boolean} [smallDesktopStart]
 * @property {boolean} [smallDesktopCenter]
 * @property {boolean} [smallDesktopEnd]
 * @property {boolean} [smallDesktopTop]
 * @property {boolean} [smallDesktopMiddle]
 * @property {boolean} [smallDesktopBottom]
 * @property {boolean} [desktopStart]
 * @property {boolean} [desktopCenter]
 * @property {boolean} [desktopEnd]
 * @property {boolean} [desktopTop]
 * @property {boolean} [desktopMiddle]
 * @property {boolean} [desktopBottom]
 * @property {boolean} [fullWidth]
 * @property {string} [className]
 * @property {string} [testId]
 */

/**
 * @param {RowProps} props
 * @returns {React.JSX.Element}
 */
const Row = ({
  children,
  start,
  center,
  end,
  top,
  middle,
  bottom,
  mobileStart,
  mobileCenter,
  mobileEnd,
  mobileTop,
  mobileMiddle,
  mobileBottom,
  tabletStart,
  tabletCenter,
  tabletEnd,
  tabletTop,
  tabletMiddle,
  tabletBottom,
  smallDesktopStart,
  smallDesktopCenter,
  smallDesktopEnd,
  smallDesktopTop,
  smallDesktopMiddle,
  smallDesktopBottom,
  desktopStart,
  desktopCenter,
  desktopEnd,
  desktopTop,
  desktopMiddle,
  desktopBottom,
  fullWidth,
  className,
  testId,
}) => (
  <div
    className={`row${start ? ' start' : ''}${center ? ' center' : ''}${
      end ? ' end' : ''
    }${top ? ' top' : ''}${middle ? ' middle' : ''}${bottom ? ' bottom' : ''}
    ${mobileStart ? ' start-mobile' : ''}${
      mobileCenter ? ' center-mobile' : ''
    }${mobileEnd ? ' end-mobile' : ''}${mobileTop ? ' top-mobile' : ''}${
      mobileMiddle ? ' middle-mobile' : ''
    }${mobileBottom ? ' bottom-mobile' : ''}
    ${tabletStart ? ' start-tablet' : ''}${
      tabletCenter ? ' center-tablet' : ''
    }${tabletEnd ? ' end-tablet' : ''}${tabletTop ? ' top-tablet' : ''}${
      tabletMiddle ? ' middle-tablet' : ''
    }${tabletBottom ? ' bottom-tablet' : ''} ${
      smallDesktopStart ? ' start-smallDesktop' : ''
    }${smallDesktopCenter ? ' center-smallDesktop' : ''}${
      smallDesktopEnd ? ' end-smallDesktop' : ''
    }${smallDesktopTop ? ' top-smallDesktop' : ''}${
      smallDesktopMiddle ? ' middle-smallDesktop' : ''
    }${smallDesktopBottom ? ' bottom-smallDesktop' : ''}
    ${desktopStart ? ' start-desktop' : ''}${
      desktopCenter ? ' center-desktop' : ''
    }${desktopEnd ? ' end-desktop' : ''}${desktopTop ? ' top-desktop' : ''}${
      desktopMiddle ? ' middle-desktop' : ''
    }${desktopBottom ? ' bottom-desktop' : ''}${
      fullWidth ? ' full-width' : ''
    } ${className}`}
    data-testid={testId}
  >
    {children}
  </div>
);

Row.propTypes = {
  children: PropTypes.node.isRequired,
  start: PropTypes.bool,
  center: PropTypes.bool,
  end: PropTypes.bool,
  top: PropTypes.bool,
  middle: PropTypes.bool,
  bottom: PropTypes.bool,
  mobileStart: PropTypes.bool,
  mobileCenter: PropTypes.bool,
  mobileEnd: PropTypes.bool,
  mobileTop: PropTypes.bool,
  mobileMiddle: PropTypes.bool,
  mobileBottom: PropTypes.bool,
  tabletStart: PropTypes.bool,
  tabletCenter: PropTypes.bool,
  tabletEnd: PropTypes.bool,
  tabletTop: PropTypes.bool,
  tabletMiddle: PropTypes.bool,
  tabletBottom: PropTypes.bool,
  smallDesktopStart: PropTypes.bool,
  smallDesktopCenter: PropTypes.bool,
  smallDesktopEnd: PropTypes.bool,
  smallDesktopTop: PropTypes.bool,
  smallDesktopMiddle: PropTypes.bool,
  smallDesktopBottom: PropTypes.bool,
  desktopStart: PropTypes.bool,
  desktopCenter: PropTypes.bool,
  desktopEnd: PropTypes.bool,
  desktopTop: PropTypes.bool,
  desktopMiddle: PropTypes.bool,
  desktopBottom: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string,
};

Row.defaultProps = {
  start: false,
  center: false,
  end: false,
  top: false,
  middle: false,
  bottom: false,
  mobileStart: false,
  mobileCenter: false,
  mobileEnd: false,
  mobileTop: false,
  mobileMiddle: false,
  mobileBottom: false,
  tabletStart: false,
  tabletCenter: false,
  tabletEnd: false,
  tabletTop: false,
  tabletMiddle: false,
  tabletBottom: false,
  smallDesktopStart: false,
  smallDesktopCenter: false,
  smallDesktopEnd: false,
  smallDesktopTop: false,
  smallDesktopMiddle: false,
  smallDesktopBottom: false,
  desktopStart: false,
  desktopCenter: false,
  desktopEnd: false,
  desktopTop: false,
  desktopMiddle: false,
  desktopBottom: false,
  fullWidth: false,
  className: '',
  testId: undefined,
};

/**
 * @typedef {Object} ColumnProps
 * @property {React.ReactNode} children
 * @property {number | string} [col]
 * @property {number | string} [offset]
 */

/**
 * @param {ColumnProps} props
 * @returns {React.JSX.Element}
 */
const Column = ({ offset, col, children }) => (
  <div
    className={`${col ? `col-${col}` : 'col'}${
      offset ? ` col-offset-${offset}` : ''
    }`}
  >
    {children}
  </div>
);

Column.propTypes = {
  children: PropTypes.node.isRequired,
  col: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Column.defaultProps = {
  col: undefined,
  offset: undefined,
};

/**
 * @typedef {Object} ResponsiveColumnProps
 * @property {React.ReactNode} [children]
 * @property {number | string} [mobileCol]
 * @property {number | string} [tabletCol]
 * @property {number | string} [smallDesktopCol]
 * @property {number | string} [desktopCol]
 * @property {number | string} [mobileOffset]
 * @property {number | string} [tabletOffset]
 * @property {number | string} [smallDesktopOffset]
 * @property {number | string} [desktopOffset]
 * @property {boolean} [mobileEnd]
 * @property {string} [className]
 */

/**
 * @param {ResponsiveColumnProps} props
 * @returns {React.JSX.Element}
 */
const ResponsiveColumn = ({
  mobileCol,
  tabletCol,
  smallDesktopCol,
  desktopCol,
  mobileOffset,
  tabletOffset,
  smallDesktopOffset,
  desktopOffset,
  mobileEnd,
  children,
  className,
}) => (
  <div
    className={`col-mobile-${mobileCol} col-tablet-${tabletCol} col-smallDesktop-${smallDesktopCol} col-desktop-${desktopCol} ${
      mobileEnd ? ' end-mobile' : ''
    }
    ${mobileOffset ? ` col-mobile-offset-${mobileOffset}` : ''}
    ${tabletOffset ? ` col-tablet-offset-${tabletOffset}` : ''}
    ${
      smallDesktopOffset ? ` col-smallDesktop-offset-${smallDesktopOffset}` : ''
    }
    ${desktopOffset ? ` col-desktop-offset-${desktopOffset}` : ''}
    ${className}`}
  >
    {children}
  </div>
);

ResponsiveColumn.propTypes = {
  children: PropTypes.node,
  mobileCol: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tabletCol: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  smallDesktopCol: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  desktopCol: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mobileOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tabletOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  smallDesktopOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  desktopOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mobileEnd: PropTypes.bool,
  className: PropTypes.string,
};

ResponsiveColumn.defaultProps = {
  mobileOffset: undefined,
  tabletOffset: undefined,
  smallDesktopOffset: undefined,
  desktopOffset: undefined,
  mobileEnd: false,
  className: '',
};

export {
  /** @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`. */
  Container,
  /** @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`. */
  Row,
  /** @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`. */
  Column,
  /** @deprecated `@summer/ui-components` is end-of-life; prefer `@summer/ui-mosaic`. */
  ResponsiveColumn,
};
